import { compact, groupBy, sortBy, uniq } from 'es-toolkit';

import dayjs from '@skynet/formatting-date';
import type { Dayjs } from '@skynet/formatting-date';

import type {
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryItem,
} from 'src/store/shared/balance-history/types';
import type { LineChartDataEntry, LineColorOptsType } from 'src/store/utils/color';
import { getLightness, getLineColor, getSaturation } from 'src/store/utils/color';

import type { GetLinearDataParams, LineChartTokenAccountData } from '../core/types';
import { getTotalChart } from 'src/store/utils/balances/calculations';
import { getPrice } from 'src/store/utils/balances/core';

const extractStringFieldFromJson = (s: string, key: string): string | undefined => {
  try {
    const m = new RegExp(`"${key}"\\s*:\\s*"([^"]*)"`).exec(s);
    return m ? m[1] : undefined;
  } catch {
    return undefined;
  }
};

type AnyLineEntry = LineChartDataEntry<unknown>;

type SeriesMeta = {
  key: string;
  /**
   * Chart coordinates of every balance snapshot (backend or live) of the
   * account(s) behind the series, sorted — the moments their balance is known.
   */
  snapshotTimes: number[];
};

const getTotalObj = (
  name: 'Total' | 'Subtotal' | 'Summary',
  token: string,
  data: {
    time: number;
    volume: number;
    volumeUSD: number;
    details: {
      token: string;
      volume: number;
      volumeUSD: number;
      price: number;
    }[];
  }[],
  lineObj: LineColorOptsType,
  tokenType: 'Base' | 'Quote',
) => ({
  id: `${name} (${token})`,
  currency: 'USD',
  tokenType,
  color: getLineColor(lineObj),
  data,
});

const getTokenAccountObj = (
  token: string,
  data: LineChartDataEntry<unknown>[],
  coordinatesData: {
    time: number;
    volume: number;
    volumeUSD: number;
    details: {
      token: string;
      volume: number;
      volumeUSD: number;
      price: number;
    }[];
  }[],
): AnyLineEntry[] => {
  return data.map((entry) => {
    return {
      ...entry,
      id: `tokens_${entry.id}`,
      currency: 'USD',
      data: coordinatesData,
    } satisfies LineChartDataEntry<Record<string, unknown>>;
  });
};

const getTotalAccountObj = (
  data: LineChartDataEntry<SeriesMeta>[],
  coordinatesData: {
    time: number;
    volume: number;
    volumeUSD: number;
    details: {
      token: string;
      volume: number;
      volumeUSD: number;
      price: number;
    }[];
  }[],
): AnyLineEntry => {
  const first = data[0];
  const parsedFirstId = first ? extractStringFieldFromJson(first.id, 'id') : undefined;
  return {
    id: `accounts_${first?.id ?? parsedFirstId ?? 'summary'}`,
    label: 'Summary (usd)',
    currency: 'USD',
    color: first?.color,
    data: coordinatesData,
  } satisfies LineChartDataEntry<LineChartTokenAccountData>;
};

const getAccountObj = (accountData: LineChartDataEntry<SeriesMeta>[]): AnyLineEntry[] => {
  return accountData.map((entry, index): LineChartDataEntry<Record<string, unknown>> => {
    return {
      ...entry,
      id: `${entry.id ?? ''}_${index}`,
      currency: 'USD',
    };
  });
};

const createLineChartDataEntry = (
  key: string,
  label: string,
  exchangeName: string | undefined,
  exchangeId: number | undefined,
  coordinates: {
    time: number;
    volume: number;
    volumeUSD: number;
    details: {
      token: string;
      volume: number;
      volumeUSD: number;
      price: number;
    }[];
  }[],
  snapshotTimes: number[],
  color: string,
  tokenType?: 'Base' | 'Quote',
  token?: string,
): LineChartDataEntry<SeriesMeta> => {
  const tokenValue = token === 'USDT' ? 'USD' : (token ?? '');
  let labelSuffix = '';

  if (tokenType) {
    if (tokenType === 'Quote') {
      labelSuffix = tokenValue !== 'USD' ? `(${tokenValue})` : '(Stables)';
    }
    if (tokenType === 'Base') {
      labelSuffix = tokenValue ? `(${tokenValue})` : '';
    }
  }

  return {
    id: `${key}_${token ?? label}`,
    label: `${label} ${labelSuffix}`,
    token: tokenValue,
    exchangeName: exchangeName ?? '',
    exchangeId: exchangeId ?? 0,
    currency: 'USD',
    tokenType,
    color: color,
    data: sortBy(coordinates, ['time']),
    meta: {
      key,
      snapshotTimes,
    },
  } satisfies LineChartDataEntry<SeriesMeta>;
};

const NAIVE_LOCAL_FORMAT = 'YYYY-MM-DD HH:mm';

// The chart's x-axis is a *fake-UTC* space: every coordinate encodes the user's
// local calendar-day digits, read back with dayjs.utc so no offset is applied.
// startDate/endDate and the synthetic "current" history item are already naive
// local strings (see `NaiveLocalDateTime` in balance-history/helpers.ts), but a
// real persisted snapshot carries a genuine Z-suffixed UTC instant, so it must
// be converted to local digits first — otherwise it lands on its UTC calendar
// day instead of the local one the picker and the comparison summary use
// (SKY-4575). Every date entering the coordinate space must go through this
// helper: if the series points and the snapshot times they are looked up by
// disagree, they drift apart by the viewer's UTC offset, the point is never
// found and the balance drops out of the chart.
const toCoordinateTime = (date: string): Dayjs =>
  dayjs.utc(dayjs(date).format(NAIVE_LOCAL_FORMAT), NAIVE_LOCAL_FORMAT);

// Inverse of `toCoordinateTime`: a chart x-coordinate is fake-UTC (the viewer's
// local calendar-day digits with no offset applied), so reading it back with
// plain `dayjs.unix(time)` re-applies the viewer's UTC offset on top and shows
// the wrong wall-clock time (SKY-4575) — read it back as UTC digits instead.
export const fromCoordinateTime = (time: number): Dayjs => dayjs.unix(time).utc();

// Snapshots this close together are the same moment (e.g. accounts snapshotted
// a few seconds apart, rounded to different minutes).
const SNAPSHOT_MATCH_TOLERANCE = 5 * 60;

// Chart x-coordinates: one tick per day from startDate to endDate, plus the
// newest snapshot within the chart's end — normally the live balance at "now" —
// so the line ends on the end balance. The newest snapshot is taken across all
// accounts, not as the last item of the flattened history: that was whichever
// account and token happened to come last, so the live point came and went
// between balance updates (SKY-4575). It is bounded by the end so that a
// specific end snapshot time ends the line on that snapshot rather than on a
// later one the end cut then removes. Ticks before the first snapshot are
// dropped: no balance is known there yet, and plotting 0 would read as an empty
// balance.
const getCoordinates = (
  startDate: string | undefined,
  endDate: string | undefined,
  snapshotTimes: number[],
  endTimestamp: number | undefined,
): { time: number }[] => {
  if (!snapshotTimes.length) {
    return [];
  }

  const coordinates: number[] = [];

  if (startDate && endDate) {
    let mCurrentDate = toCoordinateTime(startDate);
    const mEndDate = toCoordinateTime(endDate);

    while (mCurrentDate.isBefore(mEndDate) || mCurrentDate.isSame(mEndDate, 'day')) {
      coordinates.push(mCurrentDate.unix());
      mCurrentDate = mCurrentDate.add(1, 'days');
    }
  }

  const firstSnapshotTime = snapshotTimes.reduce((min, time) => Math.min(min, time), Infinity);
  const latestSnapshotTime = snapshotTimes
    .filter((time) => endTimestamp === undefined || time <= endTimestamp)
    .reduce((max, time) => Math.max(max, time), -Infinity);

  if (Number.isFinite(latestSnapshotTime)) {
    coordinates.push(latestSnapshotTime);
  }

  return uniq(coordinates)
    .filter((time) => time + SNAPSHOT_MATCH_TOLERANCE >= firstSnapshotTime)
    .sort((a, b) => a - b)
    .map((time) => ({ time }));
};

const calculateData = (
  data: JsonBalanceHistoryExtended[],
  ignoreTokens: string[],
  startDate?: string,
  tokenType?: 'Base' | 'Quote',
) => {
  if (tokenType) {
    return calculateByTokenType(data, ignoreTokens, startDate, tokenType);
  }
  return calculateGrouped(data, ignoreTokens, startDate, tokenType);
};

type SeriesTimeline = {
  /** Chart coordinate of a date of the series. */
  toTime: (date: string) => number;
  /**
   * Chart coordinates of the balance snapshots behind the series, sorted.
   * Taken from `snapshotDates` (backend + live snapshots) rather than the asset
   * history, which also carries single-token points synthesised from
   * transactions — those are not a snapshot of the whole account.
   */
  snapshotTimes: number[];
};

// Places the dates of one series on the chart. The start tick shows the start
// day's balance, so the first snapshot of that day is pulled back onto it —
// only that one: pulling every snapshot of the day onto the tick summed them
// into a multiple of the balance. Not when the range starts today, where the
// only snapshot may be the live one, which belongs at "now". "Today" is judged
// in the coordinate space: against the real "now" instant it is off by the
// viewer's UTC offset (SKY-4575).
const getSeriesTimeline = (
  items: JsonBalanceHistoryExtended[],
  startDate?: string,
): SeriesTimeline => {
  const snapshotTimes = uniq(
    items.flatMap((item) =>
      (
        item.snapshotDates ??
        item.assets.flatMap((asset) => (asset.history ?? []).map(({ date }) => date))
      ).map((date) => toCoordinateTime(date).unix()),
    ),
  ).sort((a, b) => a - b);

  const start = startDate ? toCoordinateTime(startDate) : undefined;
  const today = toCoordinateTime(dayjs().format(NAIVE_LOCAL_FORMAT));
  const startDaySnapshotTime =
    start && !start.isSame(today, 'day')
      ? snapshotTimes.find(
          (time) => time >= start.unix() && fromCoordinateTime(time).isSame(start, 'day'),
        )
      : undefined;

  const pullToStart = (time: number) =>
    start && time === startDaySnapshotTime ? start.unix() : time;

  return {
    toTime: (date) => pullToStart(toCoordinateTime(date).unix()),
    snapshotTimes: snapshotTimes.map(pullToStart),
  };
};

const createCoordinates = (
  filteredHistory: JsonBalanceHistoryItem[],
  toTime: SeriesTimeline['toTime'],
) => {
  const groupedByDate = groupBy(filteredHistory, ({ date }) => toTime(date));

  return Object.entries(groupedByDate).map(([dateKey, entries]) => ({
    details: entries.map((entry) => ({
      token: entry.token,
      volume: entry.volume,
      volumeUSD: getTotalChart([entry]),
      price: getPrice({
        price: entry.lastPrice,
        quotePrice: entry.quotePrice,
        type: entry.type,
        token: entry.token,
      }),
    })),
    time: Number(dateKey),
    volume: entries.reduce((acc, val) => acc + val.volume, 0),
    volumeUSD: getTotalChart(entries),
  }));
};

const getColorStyling = (index: number, tokenType?: 'Base' | 'Quote') => {
  const saturation = getSaturation(index);
  let lightness = getLightness(index);

  if (tokenType === 'Base') {
    lightness += 25;
  }

  return { saturation, lightness };
};

const calculateByTokenType = (
  data: JsonBalanceHistoryExtended[],
  ignoreTokens: string[],
  startDate?: string,
  tokenType?: 'Base' | 'Quote',
) => {
  return data.map((item, index) => {
    const {
      account,
      exchange,
      assets,
      key,
      hue,
      quoteToken = tokenType,
      baseToken = tokenType,
      accountName,
    } = item;

    const label = accountName ?? account?.label ?? key;
    const accountId = account?.exchange;

    const filteredHistory = assets.flatMap((asset) => {
      if (asset.type?.toLowerCase() !== tokenType?.toLowerCase()) {
        return [];
      }
      return asset.history?.filter(({ token }) => !ignoreTokens.includes(token)) ?? [];
    });

    const { toTime, snapshotTimes } = getSeriesTimeline([item], startDate);
    const coordinates = createCoordinates(filteredHistory, toTime);
    const { saturation, lightness } = getColorStyling(index, tokenType);

    return createLineChartDataEntry(
      key,
      label,
      exchange?.name,
      item.exchangeID ?? accountId,
      coordinates,
      snapshotTimes,
      getLineColor({ hue, saturation, lightness }),
      tokenType,
      tokenType === 'Base' ? baseToken : quoteToken,
    );
  });
};

// Calculate grouped data
const calculateGrouped = (
  data: JsonBalanceHistoryExtended[],
  ignoreTokens: string[],
  startDate?: string,
  tokenType?: 'Base' | 'Quote',
) => {
  const groupedData = groupBy(data, (item) => item.accountName ?? item.account?.label ?? item.key);

  return Object.entries(groupedData).map(([label, items]) => {
    const index = data.findIndex((account) => account.accountName === label);
    const { isWallet, exchange, accountName, exchangeName, hue } = items[0];

    // Combine base and quote history
    const allHistory = items.flatMap((item) => {
      const baseHistory = item.assets.find((asset) => asset.type === 'base')?.history ?? [];
      const quoteHistory = item.assets.find((asset) => asset.type === 'quote')?.history ?? [];
      return [...baseHistory, ...quoteHistory];
    });

    const filteredHistory = allHistory.filter(({ token }) => !ignoreTokens.includes(token));
    const { toTime, snapshotTimes } = getSeriesTimeline(items, startDate);
    const coordinates = createCoordinates(filteredHistory, toTime);
    const { saturation, lightness } = getColorStyling(index, tokenType);

    const exchangeItemName = isWallet ? exchangeName : exchange?.name;
    const labelItem = accountName ?? label;
    const exchangeIdItem = exchange?.id ?? items[0].account?.exchange;

    return createLineChartDataEntry(
      label,
      labelItem,
      exchangeItemName,
      exchangeIdItem,
      coordinates,
      snapshotTimes,
      getLineColor({ hue, saturation, lightness }),
      tokenType,
    );
  });
};

type PointDetail = { token: string; volume: number; volumeUSD: number; price: number };

// A series contributes its value *as of* the coordinate: its point at the
// latest snapshot of its account taken at or before it. It used to take only a
// point within ±5 minutes of the coordinate, or 0 — so today's 00:00 tick,
// whose only data is the live balance at "now", plotted a zero, and a
// coordinate only some accounts had data for summed to a fraction of the total
// (SKY-4575). The snapshot is picked per account, not per series, so an asset
// type missing from that snapshot contributes 0 rather than a stale value.
const getPointAsOf = (item: LineChartDataEntry<SeriesMeta>, time: number) => {
  const asOf = item.meta?.snapshotTimes.findLast(
    (snapshotTime) => snapshotTime <= time + SNAPSHOT_MATCH_TOLERANCE,
  );

  return asOf === undefined ? undefined : item.data.find((point) => point.time === asOf);
};

const calculatePoint = (xCoordinates: { time: number }[], data: LineChartDataEntry<SeriesMeta>[]) =>
  xCoordinates.map(({ time }) => {
    const initialSum: { details: Record<string, PointDetail>; volume: number; volumeUSD: number } =
      {
        details: {},
        volume: 0,
        volumeUSD: 0,
      };
    const result = data.reduce((sum, item) => {
      const basePoint = getPointAsOf(item, time);

      if (basePoint?.details) {
        basePoint.details.forEach((val) => {
          if (!sum.details[val.token]) {
            sum.details[val.token] = {
              token: val.token,
              volume: 0,
              volumeUSD: 0,
              price: 0,
            };
          }
          sum.details[val.token].volume += val.volume;
          sum.details[val.token].volumeUSD += val.volumeUSD;
          sum.details[val.token].price += val.price;
        });
      }

      return {
        volume: sum.volume + (basePoint ? basePoint.volume : 0),
        volumeUSD: sum.volumeUSD + (basePoint ? basePoint.volumeUSD : 0),
        details: sum.details,
      };
    }, initialSum);

    return {
      time,
      volume: result.volume,
      volumeUSD: result.volumeUSD,
      details: Object.values(result.details),
    };
  });

// With the end time on "Current", endDate only bounds the day: on first load it
// is the moment the page mounted, while the live point is re-dated on every
// balance update, so cutting at it dropped the live balance within a minute
// (SKY-4575). A specific end snapshot time still bounds the chart exactly.
const getEndTimestamp = (endDate?: string, endTime?: string): number | undefined => {
  if (!endDate) {
    return undefined;
  }

  const end = toCoordinateTime(endDate);
  return (endTime === 'current' ? end.endOf('day') : end).unix();
};

export const getLinearData = ({
  data,
  selection,
  viewChartGroupBy,
  ignoreTokens,
  viewCumulativeBalance,
  startDate,
  endDate,
  endTime,
}: GetLinearDataParams): LineChartDataEntry<JsonBalanceHistoryExtended>[] => {
  const filteredData = data.filter(
    (item) => !item.isTotalDoubling && (!selection?.length || selection.includes(item.key)),
  );

  if (!filteredData.length) {
    return [];
  }

  const endTimestamp = getEndTimestamp(endDate, endTime);

  const accountData = calculateData(filteredData, ignoreTokens, startDate);
  const coordinates = getCoordinates(
    startDate,
    endDate,
    accountData.flatMap((entry) => entry.meta?.snapshotTimes ?? []),
    endTimestamp,
  );
  const accountDataCordinates = calculatePoint(coordinates, accountData);
  const accountChartData = getAccountObj(accountData);
  const accountSummaryChartData = getTotalAccountObj(accountData, accountDataCordinates);

  const baseData = calculateData(filteredData, ignoreTokens, startDate, 'Base');
  const quoteData = calculateData(filteredData, ignoreTokens, startDate, 'Quote');

  const subtotalBaseCoordinates = calculatePoint(coordinates, baseData);

  const assets = filteredData.flatMap((item) => item.assets);

  const baseTokens = assets.filter((asset) => asset.type === 'base');
  const baseTokensMapped = baseTokens.map((token) => token.token);
  const uniqueBaseTokens = uniq(baseTokensMapped);

  const quoteTokens = assets.filter((asset) => asset.type === 'quote');
  const quoteTokensMapped = quoteTokens.map((token) => token.token);
  const uniqueQuoteTokens = uniq(quoteTokensMapped);

  let totalBaseName = 'Base';

  if (uniqueBaseTokens.length === 1 && uniqueBaseTokens[0]) {
    totalBaseName = uniqueBaseTokens[0];
  } else if (uniqueBaseTokens.length > 1) {
    totalBaseName = 'Bases';
  }

  let totalQuoteName = 'Quote';

  if (uniqueQuoteTokens.length === 1 && uniqueQuoteTokens[0]) {
    totalQuoteName = uniqueQuoteTokens[0];
  } else if (uniqueQuoteTokens.length > 1) {
    totalQuoteName = 'Stables';
  }

  const subtotalBase = getTotalObj(
    'Summary',
    totalBaseName,
    subtotalBaseCoordinates,
    { hue: filteredData[0]?.hue },
    'Base',
  );

  const subtotalQuoteCoordinates = calculatePoint(coordinates, quoteData);

  const subtotalQuote = getTotalObj(
    'Summary',
    totalQuoteName,
    subtotalQuoteCoordinates,
    { hue: filteredData[0]?.hue, lightness: 50 },
    'Quote',
  );

  const totalBase = getTotalObj(
    'Total',
    totalBaseName,
    subtotalBaseCoordinates,
    { hue: filteredData[0]?.hue },
    'Base',
  );

  const totalQuote = getTotalObj(
    'Total',
    totalQuoteName,
    subtotalQuoteCoordinates,
    { hue: filteredData[0]?.hue, lightness: 50 },
    'Quote',
  );

  const subtotalData = compact([subtotalBase, subtotalQuote]);

  const totalData = compact([totalBase, totalQuote]);

  const chartData: AnyLineEntry[] = [];
  for (let idx = 0; idx < filteredData.length; idx++) {
    const quote = quoteData[idx];
    const base = baseData[idx];

    const quoteLen = quote?.data?.length ?? 0;
    const baseLen = base?.data?.length ?? 0;

    if (quoteLen === 0 && base) {
      const pointsBase = calculatePoint(coordinates, [base]);
      chartData.push(...getTokenAccountObj(totalBaseName, [base], pointsBase));
      continue;
    }

    if (baseLen === 0 && quote) {
      const pointsQuote = calculatePoint(coordinates, [quote]);
      chartData.push(...getTokenAccountObj(totalQuoteName, [quote], pointsQuote));
      continue;
    }

    if (base) {
      const pointsBase = calculatePoint(coordinates, [base]);
      chartData.push(...getTokenAccountObj(totalBaseName, [base], pointsBase));
    }

    if (quote) {
      const pointsQuote = calculatePoint(coordinates, [quote]);
      chartData.push(...getTokenAccountObj(totalQuoteName, [quote], pointsQuote));
    }

    if (quoteLen === 0 && baseLen === 0) {
      if (Array.isArray(totalData)) {
        chartData.push(...(totalData as AnyLineEntry[]));
      }
    }
  }

  let tokensLinechartData: AnyLineEntry[];
  if (selection?.length) {
    tokensLinechartData = !viewCumulativeBalance ? [...subtotalData] : [...chartData];
  } else {
    tokensLinechartData = [...totalData];
  }

  let accountLinechartData: AnyLineEntry[];
  if (!selection.length) {
    accountLinechartData = [
      {
        id: 'Summary (USD)',
        currency: 'USD',
        color: getLineColor({ hue: filteredData[0]?.hue, lightness: 50 }),
        data: accountDataCordinates,
      },
    ];
  } else {
    accountLinechartData = viewCumulativeBalance
      ? accountChartData
      : compact([accountSummaryChartData]);
  }

  const linechartData = viewChartGroupBy === 'tokens' ? tokensLinechartData : accountLinechartData;

  const filtered = linechartData.filter(
    (item) => !item.data.every(({ volume, volumeUSD }) => volume === 0 && volumeUSD === 0),
  );

  return filtered.map(({ id, data, color, label, tokenType }) => ({
    id,
    data: endTimestamp === undefined ? data : data.filter(({ time }) => time <= endTimestamp),
    color,
    label,
    tokenType,
    meta: undefined,
  }));
};
